import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { useLogout, useRefreshToken } from "@/api/AuthApi";

// ⚠️ 15 minutes matches the spec's stated session duration. Adjust here if
// the real requirement differs — this is the single source of truth.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_DURATION_MS = 60 * 1000; // show the warning modal 60s before logout

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
];

const SessionManager: React.FC = () => {
  const { user } = useAuthStore();
  const { logOutUser } = useLogout();
  const { refreshToken } = useRefreshToken();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION_MS / 1000);
  const stayConnectedButtonRef = useRef<HTMLButtonElement>(null);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
  }, []);

  const startTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setSecondsLeft(WARNING_DURATION_MS / 1000);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(WARNING_DURATION_MS / 1000);

      countdownIntervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_DURATION_MS);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      logOutUser();
    }, IDLE_TIMEOUT_MS);
  }, [clearAllTimers, logOutUser]);

  const handleActivity = useCallback(() => {
    // Once the warning modal is up, activity alone shouldn't silently
    // dismiss it — only an explicit "stay connected" click should.
    // Otherwise moving the mouse while reading the modal would extend
    // the session without the user actually making that choice.
    if (showWarning) return;
    startTimers();
  }, [showWarning, startTimers]);

  useEffect(() => {
    if (!showWarning) return;

    stayConnectedButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleStayConnected();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWarning]);

  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    startTimers();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity),
    );

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStayConnected = async () => {
    await refreshToken();
    startTimers();
  };

  const handleLogoutNow = () => {
    clearAllTimers();
    logOutUser();
  };

  if (!user || !showWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
      aria-describedby="session-warning-desc"
    >
      <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 text-center">
        <h2 id="session-warning-title" className="text-lg font-semibold mb-2">
          Votre session va bientôt expirer
        </h2>
        <p id="session-warning-desc" className="text-sm text-gray-600 mb-4">
          Pour des raisons de sécurité, vous allez être déconnecté dans{" "}
          <span className="font-semibold text-red-500">
            {minutes}:{seconds.toString().padStart(2, "0")}
          </span>
          . Souhaitez-vous rester connecté ?
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            ref={stayConnectedButtonRef}
            onClick={handleStayConnected}
            className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
          >
            Rester connecté
          </button>
          <button
            type="button"
            onClick={handleLogoutNow}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
